// Roles & permissions: built-in roles plus custom ones, each a set of permissions.
import { api } from "../lib/api";
import { t, tl } from "../lib/i18n";
import { can } from "../lib/session";
import { h, invalidate, local, resource } from "../lib/ui";
import { act, Button, Chip, confirmDialog, Dialog, Field, formData, isBusy, Loading, LoadError, openDialog } from "../shared/components";
import { PageHead } from "./app";

interface Role {
  key: string;
  name: { en: string; fr: string };
  permissions: string[];
  system: boolean;
  members: number;
}

function newRoleDialog(onDone: (key: string) => void) {
  openDialog((close) => (
    <Dialog title={t("admin.roles.newTitle")} onClose={close}>
      <form class="stack" onSubmit={(e: Event) => {
        const d = formData(e);
        act("roles.create", async () => {
          await api.post("/api/admin/roles", { key: d.key, name: { en: d.en, fr: d.fr }, permissions: [] });
          close();
          onDone(d.key);
        }, t("admin.roles.created"));
      }}>
        <div class="cols-2">
          <Field label={t("admin.roles.nameEn")} id="r-en"><input class="input" id="r-en" name="en" required maxLength={40} /></Field>
          <Field label={t("admin.roles.nameFr")} id="r-fr"><input class="input" id="r-fr" name="fr" required maxLength={40} /></Field>
        </div>
        <Field label={t("admin.roles.key")} id="r-key" hint={t("admin.roles.keyHint")}>
          <input class="input" id="r-key" name="key" required pattern="[a-z][a-z0-9_]{1,40}" maxLength={41} />
        </Field>
        <div class="row end">
          <Button kind="secondary" onClick={close}>{t("common.cancel")}</Button>
          <Button type="submit" busy={isBusy("roles.create")}>{t("admin.roles.create")}</Button>
        </div>
      </form>
    </Dialog>
  ));
}

export function RolesPage() {
  const r = resource("admin.roles", () => api.get("/api/admin/roles"));
  const s = local("screen:roles", () => ({ selected: "", draft: null as Role | null }));
  if (r.error && !r.data) return <LoadError error={r.error} retry={() => r.reload()} />;
  if (!r.data) return <Loading rows={6} />;
  const roles: Role[] = r.data.roles;
  const catalog: { key: string; group: string }[] = r.data.catalog;
  const current = roles.find((x) => x.key === s.selected) ?? roles.find((x) => !x.permissions.includes("*")) ?? roles[0];
  const draft = s.draft && s.draft.key === current.key ? s.draft : null;
  const role = draft ?? current;
  const locked = role.permissions.includes("*");
  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(current);
  const iHold = (p: string) => can(p);

  const toggle = (perm: string, on: boolean) => {
    const base = draft ?? JSON.parse(JSON.stringify(current));
    base.permissions = on ? [...base.permissions, perm] : base.permissions.filter((p: string) => p !== perm);
    s.set({ draft: base });
  };
  const rename = (lang: "en" | "fr", v: string) => {
    const base = draft ?? JSON.parse(JSON.stringify(current));
    base.name = { ...base.name, [lang]: v };
    s.set({ draft: base });
  };
  const save = () => act("roles.save", async () => {
    await api.put(`/api/admin/roles/${role.key}`, { name: role.name, permissions: role.permissions });
    s.set({ draft: null });
    invalidate("admin.roles");
  }, t("admin.roles.saved"));
  const remove = async () => {
    if (!(await confirmDialog({ title: t("admin.roles.deleteTitle"), body: t("admin.roles.deleteBody", { name: tl(role.name) }), confirm: t("common.delete"), danger: true }))) return;
    act("roles.delete", async () => {
      await api.del(`/api/admin/roles/${role.key}`);
      s.set({ selected: "", draft: null });
      invalidate("admin.roles");
    }, t("admin.roles.deleted"));
  };

  const groups = ["admin", "operations", "field"];
  return (
    <div class="stack loose">
      <PageHead title={t("admin.roles.title")} intro={t("admin.roles.intro")}
        actions={<Button icon="plus" onClick={() => newRoleDialog((key) => { s.set({ selected: key, draft: null }); invalidate("admin.roles"); })}>{t("admin.roles.new")}</Button>} />
      <div style="display:grid;grid-template-columns:minmax(220px,280px) minmax(0,1fr);gap:20px;align-items:start" class="roles-layout">
        <div class="panel role-list" role="list">
          {roles.map((x) => (
            <button role="listitem" aria-pressed={String(x.key === role.key)} onClick={() => s.set({ selected: x.key, draft: null })}>
              <span>{tl(x.name)}</span>
              <span class="xs muted tabular">{t("admin.roles.members", { count: x.members })}</span>
            </button>
          ))}
        </div>
        <section class="panel">
          <div class="panel-head">
            <div class="stack tight" style="gap:2px">
              <h2>{tl(role.name)}</h2>
              <span class="xs muted">{role.system ? t("admin.roles.builtIn") : t("admin.roles.custom")}</span>
            </div>
            {!role.system ? <Button kind="danger-outline" size="small" disabled={role.members > 0} title={role.members ? t("admin.roles.inUse") : ""} onClick={remove}>{t("common.delete")}</Button> : null}
          </div>
          <div class="panel-body stack loose">
            {locked ? (
              <div class="note">{t("admin.roles.ownerNote")}</div>
            ) : (
              [
                <div class="cols-2">
                  <Field label={t("admin.roles.nameEn")} id="rn-en"><input class="input" id="rn-en" value={role.name.en} maxLength={40} onInput={(e: Event) => rename("en", (e.target as HTMLInputElement).value)} /></Field>
                  <Field label={t("admin.roles.nameFr")} id="rn-fr"><input class="input" id="rn-fr" value={role.name.fr} maxLength={40} onInput={(e: Event) => rename("fr", (e.target as HTMLInputElement).value)} /></Field>
                </div>,
                ...groups.map((g) => (
                  <div class="stack tight">
                    <h3 class="small" style="font-family:var(--font)">{t(`admin.roles.groups.${g}`)}</h3>
                    <div class="perm-grid">
                      {catalog.filter((p) => p.group === g).map((p) => (
                        <label>
                          <input type="checkbox" checked={role.permissions.includes(p.key)} disabled={!iHold(p.key)}
                            onChange={(e: Event) => toggle(p.key, (e.target as HTMLInputElement).checked)} />
                          <span class="stack tight" style="gap:2px">
                            <span class="strong">{t(`perms.${p.key}.label`)}</span>
                            <span class="small muted">{t(`perms.${p.key}.help`)}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )),
                <div class="note small">{t("admin.roles.customerNote")}</div>,
              ]
            )}
          </div>
          {!locked ? (
            <div class="panel-foot">
              {dirty ? <Chip kind="accent" plain>{t("common.unsaved")}</Chip> : null}
              <Button kind="secondary" disabled={!dirty} onClick={() => s.set({ draft: null })}>{t("common.discard")}</Button>
              <Button disabled={!dirty} busy={isBusy("roles.save")} onClick={save}>{t("admin.roles.save")}</Button>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
